/* jshint maxcomplexity: 5, maxparams: 3, maxdepth: 2, maxstatements: 10 */

'use strict';

const priceSubscriberMixin = require('../mixins/price_subscriber.mixin');
const creditCardTypes = require('../../modules/data/credit_card_types');
const PB_ITEM_TYPE = require('../enums/pricebreakdown_item_type.enum');
const insuranceCatalogueCollection = require('../../modules/collections/insurance_catalogue.collection').getInstance();

module.exports = Odigeo.Model.extend(priceSubscriberMixin.extend({
    connect: true,
    defaults: {
        'savingAlertsTracking': false,
        'isAutoApplyDiscount': false,
    },

    dataURL: null,
    agreeConditionsUrl: 'iagreeconditions',

    init() {
        this.refreshData();
        this.bindPriceCollection();
        this.on('priceCollection.repaintPrices', this.reloadPriceViews);
    },

    // Load iagreeconditions because it needs to refresh JSP constiables
    getDataRequestSettings() {
        return {
            type: 'GET',
            cache: false,
            success: this.requestSuccessHandler.bind(this),
        };
    },

    url() {
        return Odigeo.Settings.getProperty('widgetPartUrlFormatter')(this.widget.name, this.agreeConditionsUrl);
    },

    requestSuccessHandler(msg) {
        this.trigger('renderAgreeConditions', msg);
    },

    refreshData() {
        this.isEmptyData = false;
        this.refresh();
    },

    reloadPriceViews() {
        this.trigger('updatePrices', this.getFinalPrice(), this.getPricePerPassenger());
        this.trigger('render', this.getPriceCollectionData());
    },

    getPriceBreakdownItemByType(type) {
        return this.getItemByType(type);
    },

    updateCcFee({fee = 0}) {
        const ccFee = this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE);
        ccFee.set('allPrice', fee);
        ccFee.set('onePrice', fee / this.widget.options.numtravellers);
        Odigeo.trigger(this.widget.id + ':updateSummaryPrices');
        return this.getPriceCollectionData();
    },

    getPriceSummaryData(membershipInfo) {
        let priceCollectionData = this.getPriceCollectionData();
        // Price and taxes
        const passengersPrices = this.getPassengersPrices(priceCollectionData);
        const taxesAndCharges = this.getTaxesAndChargesPrice(passengersPrices.flightTaxesAndCharges, priceCollectionData.totalFeeProvider);
        const additionalServices = this.getAdditionalServices(priceCollectionData.items, this.widget.options);
        const ccFeePrice = priceCollectionData.ccFeeItem.allPrice;
        // Discounts
        const negativeTaxesAndCharges =
        this.getNegativeTaxesAndChargesPrice(passengersPrices.flightTaxesAndCharges, priceCollectionData.totalFeeProvider);
        const dynpackDiscount = this.getDynpackDiscount(priceCollectionData);
        let edreamsDiscount = this.getEdreamsDiscount(membershipInfo.isIncludedInPrice, negativeTaxesAndCharges, ccFeePrice);
        let totalDiscount = this.getTotalDiscount(edreamsDiscount, membershipInfo, priceCollectionData);
        const ticketsPrice = passengersPrices.ticketsPrice;

        // Calculated Prices
        const hasDiscount = totalDiscount < 0;
        const standardPrice = ticketsPrice + taxesAndCharges;
        const discountedPrice = standardPrice + totalDiscount;

        return {
            additionalServices: additionalServices,
            discountedPrice: {
                displayClass: hasDiscount ? '' : 'odf-hidden',
                value: this.formatPriceIfHasValue(discountedPrice),
            },
            edreamsDiscount: {
                displayClass: edreamsDiscount != 0 ? '' : 'odf-hidden',
                formattedValue: this.formatPriceIfHasValue(edreamsDiscount),
                edreamsDiscount,
            },
            edreamsPrimeMemberDiscount: {
                edreamsPrimeMemberDiscountVisibilityClass: membershipInfo.displayFee < 0 ? '' : 'odf-hidden',
                edreamsPrimeMemberDiscountStateClass: membershipInfo.isIncludedInPrice ? 'odf-text-positive' : 'odf-text-warning',
                edreamsPrimeMemberDiscountSlashedClass: membershipInfo.slashedClass,
                value: this.formatPriceIfHasValue(membershipInfo.fee),
            },
            dynpackDiscount: {
                displayClass: dynpackDiscount < 0 ? '' : 'odf-hidden',
                value: this.formatPriceIfHasValue(dynpackDiscount),
            },
            hasAdditionalServices: this.hasAdditionalServiceGeneric(additionalServices),
            paymentMethod: this.getPaymentMethod(this.getPriceBreakdownItemByType(PB_ITEM_TYPE.CC_FEE)),
            repricing: this.getRepricing(this.getPriceBreakdownItemByType(PB_ITEM_TYPE.REPRICING)),
            standardPrice: {
                hasDiscount,
                value: this.formatPriceIfHasValue(standardPrice),
            },
            taxesAndCharges: {
                total: Odigeo.Utils.formatCurrency(taxesAndCharges),
                taxesAndChargesDetailsClass: passengersPrices.flightTaxesAndCharges <= 0 || priceCollectionData.totalFeeProvider <= 0 ?
                    'odf-hidden' : '',
                priceBreakdownNewCopy: passengersPrices.flightTaxesAndCharges <= 0 || priceCollectionData.totalFeeProvider <= 0,
                flightTaxesAndCharges: this.formatPriceIfHasValue(passengersPrices.flightTaxesAndCharges),
                servicesCharges: this.formatPriceIfHasValue(priceCollectionData.totalFeeProvider),
            },
            ticketsPrice: this.formatPriceIfHasValue(ticketsPrice),
            total: this.formatPriceIfHasValue(priceCollectionData.totalPrice),
            options: this.widget.options,
            priceCollection: priceCollectionData,
            areAllPassengersWithBaggage: this.widget.getAreAllPassengersWithBaggage(),
            baggagesDiscount: this.widget.getBaggagesDiscount(),
            showMembershipSubscription: Odigeo._.bind(this.showMembershipSubscription, this),
        };
    },

    hasAdditionalServiceGeneric(additionalServices) {
        if (Odigeo.Membership.isMembershipFreeTrial) {
            return this.hasAdditionalServicesFreeTrial(additionalServices);
        }
        return !Odigeo._.every(additionalServices, (additionalService) => {
            return typeof additionalService === 'boolean'
                ? !additionalService
                : Odigeo._.isEmpty(additionalService);
        });
    },

    hasAdditionalServicesFreeTrial(additionalServices) {
        const hasExtraService = !Odigeo._.every(additionalServices, Odigeo._.isEmpty);
        if (!hasExtraService && this.showMembershipSubscription(additionalServices.membershipSubscription)) {
            return true;
        }
        return hasExtraService;
    },

    showMembershipSubscription(membershipSubscriptionPrice) {
        const priceCollectionData = this.getPriceCollectionData();
        return priceCollectionData.membershipPerks.isSubscriptionAdded &&
            membershipSubscriptionPrice === 0;
    },

    getPaymentMethod(paymentMethodItem) {
        const concept = paymentMethodItem.get('concept');
        const method = paymentMethodItem.get('method');
        const allPrice = paymentMethodItem.get('allPrice');
        const creditCardResolved = Odigeo._.isUndefined(paymentMethodItem.get('creditCardResolved'))
            ? false
            : paymentMethodItem.get('creditCardResolved');
        const ccSelected = {
            name: '',
            value: 0,
            creditCardResolved: false,
        };
        if (concept) {
            ccSelected.name = (method === 'CREDITCARD' && !Odigeo._.isUndefined(creditCardTypes[concept]) ? creditCardTypes[concept].name : concept);
            ccSelected.creditCardResolved = (method === 'CREDITCARD' ? creditCardResolved : true);
            ccSelected.value = allPrice;
        }
        return ccSelected;
    },

    getRepricing(repricingItem) {
        return this.getPriceSummaryItemData(repricingItem ? repricingItem.get('allPrice') : 0);
    },

    // taxesAndCharges just get positive values, so we just add values if they are positive
    getTaxesAndChargesPrice(flightTaxesAndCharges, serviceCharges) {
        flightTaxesAndCharges = flightTaxesAndCharges > 0 ? flightTaxesAndCharges : 0;
        serviceCharges = serviceCharges > 0 ? serviceCharges : 0;
        return flightTaxesAndCharges + serviceCharges;
    },

    // taxesAndCharges just get positive values, so we just add values if they are positive
    getNegativeTaxesAndChargesPrice(flightTaxesAndCharges, serviceCharges) {
        flightTaxesAndCharges = flightTaxesAndCharges < 0 ? flightTaxesAndCharges : 0;
        serviceCharges = serviceCharges < 0 ? serviceCharges : 0;
        return flightTaxesAndCharges + serviceCharges;
    },

    getDynpackDiscount(priceCollectionData) {
        return Odigeo.Utils.isRequestApp('dp') || Odigeo.Utils.isRequestApp('upsell') ?
            -(priceCollectionData.dynpackDiscountFee) : 0;
    },

    getTotalDiscount(edreamsDiscount, membershipInfo, priceCollectionData) {
        const membershipDiscount = membershipInfo.isIncludedInPrice ? membershipInfo.fee : 0;
        const dynpackDiscount = this.getDynpackDiscount(priceCollectionData);

        return membershipDiscount + edreamsDiscount + dynpackDiscount;
    },

    // eDreamsDiscount just get negative values, so we just add values if they are negative
    getEdreamsDiscount(isMembershipDiscountActive, serviceCharges, ccFee) {
        let value = 0;
        const ccFeeDiscount = ccFee < 0 ? ccFee : 0;

        if (isMembershipDiscountActive) {
            value = serviceCharges < 0 ? serviceCharges : 0;
        } else {
            value = serviceCharges < 0 ? (serviceCharges + ccFeeDiscount) : ccFeeDiscount;
        }

        return value;
    },

    getAdditionalServices(items, options) {
        // Items with one line in priceSummary:
        let additionalCheckedBaggage = 0;
        let membershipSubscription = 0;
        let promoCode = 0;
        let seats = 0;
        let handBaggage = 0;
        let handBaggageLabel = '';
        // Items with one or more lines in priceSummary:
        const insurances = [];
        const additionalAncillariesProducts = [];
        const serviceOptions = [];

        Odigeo._.each(items, (item) => {
            switch (item.type) {
                case PB_ITEM_TYPE.BAGGAGES:
                case PB_ITEM_TYPE.BAGGAGES_WEIGHT:
                    additionalCheckedBaggage += item.allPrice;
                    break;
                case PB_ITEM_TYPE.MEMBERSHIP_SUBSCRIPTION:
                    membershipSubscription += item.allPrice;
                    break;
                case PB_ITEM_TYPE.PROMO_CODE:
                    promoCode += item.allPrice;
                    break;
                case PB_ITEM_TYPE.SEAT:
                case PB_ITEM_TYPE.SEATMAP:
                    seats += item.allPrice;
                    break;
                case PB_ITEM_TYPE.ASSISTANCE_INSURANCE:
                case PB_ITEM_TYPE.CANX_INSURANCE:
                case PB_ITEM_TYPE.INSURANCE:
                case PB_ITEM_TYPE.INSURANCE_IN_CART:
                case PB_ITEM_TYPE.REBOOKING_INSURANCE:
                    insurances.push(item);
                    break;
                case PB_ITEM_TYPE.ADDITION:
                case PB_ITEM_TYPE.FAST_TRACK:
                    additionalAncillariesProducts.push(item);
                    break;
                case PB_ITEM_TYPE.SERVICE_OPTIONS:
                    serviceOptions.push(item);
                    break;
                case PB_ITEM_TYPE.HAND_BAGGAGE:
                    handBaggage += item.allPrice;
                    handBaggageLabel = item.conceptLabel;
                    break;
            }
        });

        return {
            additionalCheckedBaggage: this.formatPriceIfHasValue(this.widget.getBaggagesDiscountPrice(additionalCheckedBaggage)),
            beforeDiscountAdditionalCheckedBaggages: this.formatPriceIfHasValue(additionalCheckedBaggage),
            membershipSubscription: this.formatPriceIfHasValue(membershipSubscription),
            promoCode: this.formatPriceIfHasValue(promoCode),
            seats: this.formatPriceIfHasValue(seats),
            insurances: insurances,
            additionalAncillariesProducts: additionalAncillariesProducts,
            serviceOptions: serviceOptions,
            hasBaggageIncluded: this.widget.hasAllRowsIncludedBaggages(),
            hasFreeCancellation: !insuranceCatalogueCollection.isCancellationFree() && options.showFreeCancellationMessage,
            handBaggage: this.formatPriceIfHasValue(handBaggage),
            handBaggageLabel,
        };
    },

    getPriceSummaryItemData(price) {
        return {
            displayClass: price <= 0 ? 'odf-hidden' : '',
            value: this.formatPriceIfHasValue(price),
        };
    },

    formatPriceIfHasValue(price) {
        return price !== 0 ? Odigeo.Utils.formatCurrency(price) : 0;
    },

    getPassengersPrices(priceCollectionData) {
        let ticketsPrice = 0;
        let flightTaxesAndCharges = 0;

        Odigeo._.each(priceCollectionData.items, function(item) {
            if (item.isPassengerItem) {
                ticketsPrice = ticketsPrice + (item.providerFare * item.quantity);
                flightTaxesAndCharges = flightTaxesAndCharges + (item.tax * item.quantity);
            }
        });

        return {
            ticketsPrice: ticketsPrice - this.getDynpackDiscount(priceCollectionData),
            flightTaxesAndCharges: flightTaxesAndCharges,
        };
    },

    getPriceWithoutGuaranteeFormatted() {
        const guaranteeItem = this.getPayWithGuaranteeItemInCollection();
        const guaranteePrice = guaranteeItem.get('allPrice') || 0;
        const totalPrice = this.getPriceCollectionData().totalPrice;
        return Odigeo.Utils.formatCurrency(totalPrice - guaranteePrice);
    },

    setPayWithGuaranteeTrackingConditionsData(data = {}) {
        this.set('pdfUrl', data.pdfUrl);
        this.set('policy', data.policy);
        this.set('pwgLink', data.link);
    },
}));
